import { WorkspaceNavigationService } from './services/workspace-navigation.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { auditTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { MapNavigationService } from './map/core/map-navigation.service';
import { ViewTabId } from './components/sidebar/sidebar.component';
import { rootBreadcrumbQueryParams, WorkspaceBreadcrumb } from './components/workspace/workspace-breadcrumbs.component';
import { WorkspaceBreadcrumbTrail } from './components/workspace/workspace-breadcrumb-trail.service';
import { asapScheduler } from 'rxjs';
import { observeOn } from 'rxjs/operators';
import {
  hasRoutedWorkspaceCard,
  WorkspaceOpenMode,
  workspaceOpenModeFromInfo,
} from './services/workspace-route';
import { WorkspaceLayoutService } from './services/workspace-layout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = 'best route debugging console';
  onRoutedPage = false;
  isStandaloneRoute = false;
  activeViewTab: ViewTabId | null = null;
  breadcrumbs: WorkspaceBreadcrumb[] = [];
  private routeBreadcrumbs: WorkspaceBreadcrumb[] = [];
  private pageTrail: WorkspaceBreadcrumb[] = [];
  zoom = 13;
  sidebarExpanded = window.innerWidth >= 900;
  private pendingWorkspaceOpenMode: WorkspaceOpenMode | null = null;

  get cardMaximized(): boolean {
    return this.layout.maximized;
  }

  get cardDockedRight(): boolean {
    return this.layout.dockedRight;
  }

  /** Windowed and maximized Workspaces reserve the assistant gutter. A right-
   *  docked Workspace leaves the shared Composer in its map position. */
  get workspaceHasAssistantGutter(): boolean {
    return this.onRoutedPage && !this.cardDockedRight;
  }

  constructor(
    private router: Router,
    private mapNavigation: MapNavigationService,
    private layout: WorkspaceLayoutService,
    public auth: AuthService,
    private breadcrumbTrail: WorkspaceBreadcrumbTrail,
    public contextNavigation?: WorkspaceNavigationService,
  ) {}

  onViewTabChange(id: ViewTabId): void {
    if (id === 'overview' && this.activeViewTab === 'overview') {
      void this.router.navigate(['/overview'], { queryParams: this.routeBreadcrumbs.find(crumb => crumb.path === '/overview')?.queryParams });
      return;
    }
    this.router.navigateByUrl(`/${id}`);
  }

  toggleMaximize(): void {
    this.layout.toggleMaximize();
  }

  toggleDockRight(): void {
    this.layout.toggleDockRight();
  }

  closeCard(): void {
    this.router.navigateByUrl('/');
  }

  private isStandalonePath(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path.startsWith('/sign/')
      || path.startsWith('/reset-password')
      || path.startsWith('/ui-gallery');
  }

  private updateShellMode(
    url: string,
    openMode: WorkspaceOpenMode | null = null,
  ): void {
    const path = url.split('?')[0].split('#')[0];
    this.onRoutedPage = hasRoutedWorkspaceCard(path);
    this.activeViewTab =
      path === '/overview' || path.startsWith('/overview/') ? 'overview'
      : path === '/dashboard' ? 'dashboard'
      : path === '/files' || path.startsWith('/files/') ? 'files'
      : path === '/campaigns' || path.startsWith('/campaigns/') ? 'campaigns'
      : null;
    this.routeBreadcrumbs = this.buildBreadcrumbs(path);
    this.breadcrumbs = [...this.routeBreadcrumbs, ...this.pageTrail];
    const drawsOnMap = this.routeDrawsOnMap();
    this.layout.setRoute(path, this.onRoutedPage && drawsOnMap, openMode);
  }

  /**
   * Whether the active route declares that it draws on the shared map.
   *
   * Read from the route tree rather than a list of paths here, so a page that
   * moves or is renamed carries the fact with it, and read from the LEAF: a
   * child of Territories that is not itself a map view must be able to say so.
   */
  private routeDrawsOnMap(): boolean {
    let route: ActivatedRoute | null = this.router.routerState.root.firstChild;
    let drawsOnMap = false;
    while (route) {
      const own = route.routeConfig?.data ?? {};
      if ('mapPage' in own) drawsOnMap = own['mapPage'] === true;
      route = route.firstChild;
    }
    return drawsOnMap;
  }

  /**
   * Breadcrumbs normally contain ancestors; a route may keep its entry root visible.
   * The active page names itself in its
   * content while Dashboard and its functional group remain stable ways back.
   */
  private buildBreadcrumbs(path: string): WorkspaceBreadcrumb[] {
    const trail: WorkspaceBreadcrumb[] = [];
    const segments: string[] = [];
    let group = '';
    let showGroup = true;
    // data.breadcrumbRoot names the route a page is entered from (default Dashboard).
    let entryPath = '/dashboard';
    let entryQueryParams: Record<string, string> = {};
    let showRoot = false;
    let breadcrumbLayer = '';
    let route: ActivatedRoute | null = this.router.routerState.root.firstChild;

    while (route) {
      segments.push(...route.snapshot.url.map(segment => segment.path));
      const ownData = route.routeConfig?.data ?? {};
      const title = ownData['title'];
      if (typeof title === 'string' && title) {
        trail.push({ label: title, path: `/${segments.join('/')}` });
      }
      const inheritedGroup = route.snapshot.data?.['group'];
      if (typeof inheritedGroup === 'string') group = inheritedGroup;
      if (ownData['breadcrumbGroup'] === false) showGroup = false;
      if (typeof ownData['breadcrumbRoot'] === 'string') {
        entryPath = ownData['breadcrumbRoot'];
        showRoot = ownData['breadcrumbShowRoot'] === true;
        entryQueryParams = rootBreadcrumbQueryParams(route.snapshot);
      }
      if (typeof ownData['breadcrumbLayer'] === 'string') {
        breadcrumbLayer = ownData['breadcrumbLayer'];
      }
      route = route.firstChild;
    }

    const entryTitle = this.router.config.find(r => `/${r.path}` === entryPath)?.data?.['title'];
    const crumbs: WorkspaceBreadcrumb[] = [{ label: entryTitle ?? entryPath, path: entryPath }];
    if (Object.keys(entryQueryParams).length) crumbs[0].queryParams = entryQueryParams;
    if (entryPath === '/' && breadcrumbLayer) crumbs.push({ label: breadcrumbLayer, path: '/' });
    if (entryPath === '/dashboard' && showGroup && group && group !== 'Configuration') {
      crumbs.push({ label: group, path: '/dashboard', queryParams: { group } });
    }
    // The entry may also be the route's own title (Files): list it once.
    crumbs.push(...trail.filter(crumb => !crumbs.some(entry => entry.path === crumb.path)));
    return crumbs.filter(crumb => crumb.path !== path || (showRoot && crumb.path === entryPath));
  }

  ngOnInit(): void {
    // Pages set their trail while they are being created; apply it after the
    // current change detection pass so the shell is not changed mid-check.
    this.breadcrumbTrail.trail$.pipe(observeOn(asapScheduler)).subscribe(trail => {
      this.pageTrail = trail;
      this.breadcrumbs = [...this.routeBreadcrumbs, ...trail];
    });

    this.contextNavigation?.start();
    this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
    ).subscribe(e => {
      this.isStandaloneRoute = this.isStandalonePath(e.url || '');
      this.pendingWorkspaceOpenMode = workspaceOpenModeFromInfo(
        this.router.getCurrentNavigation()?.extras.info,
      );
    });
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe(e => {
      const url: string = e.urlAfterRedirects || e.url || '';
      this.isStandaloneRoute = this.isStandalonePath(url);
      this.updateShellMode(url, this.pendingWorkspaceOpenMode);
      this.pendingWorkspaceOpenMode = null;
    });
    this.isStandaloneRoute = this.isStandalonePath(this.router.url);
    this.updateShellMode(this.router.url);

    // Leaflet attaches below this component and can change zoom while the
    // current Angular view is still being checked (for example fitBounds on
    // startup). Coalesce that map event into the next turn: one badge update,
    // no parent value changing halfway through a check.
    this.mapNavigation.zoom$.pipe(
      auditTime(0),
      distinctUntilChanged(),
    ).subscribe(z => this.zoom = z);
  }
}
